import React, { useEffect, useRef, useState } from 'react';
import {
  DateTimeSetExpr,
  RecurringWindowExpr,
  VariableValue,
} from '@/proto/logiblocks/logiblocks';
import { VariableType } from '@/proto/common';
import type {
  FieldOption,
  GroupedFieldOption,
  AlertPhaseOption,
} from '@/apis/logiblocksAPI';
import { Checkbox, Select } from 'antd';
import { CustomSelectIcon } from '@/assets/icons/icons';
import { LiteralValueEntryProps } from '../../variables/LiteralValueEntry';
import styles from '../../variables/DropdownVariablePicker.module.css';
import rhsStyles from './RhsVariablePicker.module.css';
import DateTimeConditionValueField from './CustomDateTimeDayPicker/DateTimeConditionValueField';
import { AlertTypeValuePicker } from '../../variables/AlertTypeValuePicker';
import { CustomInput } from '@/components';
import { useTeamGroups } from '@/hooks/useTeamGroups';
import {
  groupMatchesSearch,
  groupSelectionState,
  isGroupValue,
  groupIdFromValue,
  teamPickerSectionLabels,
  toGroupValue,
  toggleGroupSelection,
} from '@/common/utils/teamGroups';

/** Reusable styled tag used in both single-select labelRender and multi-select tagRender. */
function SelectTag({
  label,
  onClear,
  style,
}: {
  label: React.ReactNode;
  onClear: () => void;
  style?: React.CSSProperties;
}) {
  return (
    // eslint-disable-next-line react/forbid-dom-props -- caller-supplied spacing
    <span className={styles.selectedTag} style={style}>
      <span className={styles.selectedTagLabel}>{label}</span>
      <span
        className={styles.selectedTagClear}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClear();
        }}
      >
        ✕
      </span>
    </span>
  );
}

const { Option } = Select;

export interface RhsValuePickerProps {
  /** Type of the LHS variable — determines which input to render. */
  lhsType: VariableType | undefined;
  /** Options from the backend field config. Flat FieldOption[], grouped GroupedFieldOption[],
   *  or AlertPhaseOption[] for ALERT_TYPE fields. */
  options?: FieldOption[] | GroupedFieldOption[] | AlertPhaseOption[];
  /** When true, options are grouped: [{ label, options[] }]. Renders OptGroup headers. */
  grouped?: boolean;
  /** Current committed literal value. */
  value?: VariableValue;
  onChange: (val: VariableValue | null) => void;
  disabled?: boolean;
  whiteBackground?: boolean;
  placeholder?: string;
  /** Legacy per-day-of-week shape, read only to display an already-saved condition. */
  recurringWindowExpr?: RecurringWindowExpr;
  /** Current DateTimeSetExpr — the multi-date/multi-day, per-entry-range,
   * explicit-timezone value for a WITHIN/NOT_WITHIN condition. */
  dateTimeSetExpr?: DateTimeSetExpr;
  /** Called with the full updated DateTimeSetExpr, ready to write straight
   * to the backend. */
  onDateTimeSetExprChange?: (next: DateTimeSetExpr) => void;
  /** When true, renders a multi-select (for CONTAINS / NOT_CONTAINS / IN_LIST). */
  isMultiValue?: boolean;
  /** Current list of selected values (for multi-select operators). */
  listValue?: VariableValue[];
  /** Called when the multi-select selection changes. */
  onListChange?: (vals: VariableValue[]) => void;
  /**
   * When true, always renders a plain text input instead of the options
   * dropdown, even though `options` may be populated — used for text-search
   * operators (Contains, Starts With, Matches Regex, …) on a
   * company-defined-enum field, where the field's dropdown of existing
   * values doesn't apply to a search pattern.
   */
  forceTextInput?: boolean;
  /**
   * The LHS is the shipment ``teams`` field: the multi-select offers group
   * teams that expand to their teams among the options. Group values are
   * never stored.
   */
  isTeamsField?: boolean;
}

type SelectOption = {
  value: string;
  label: string;
};
/** A flat option, or an OptGroup when ``options`` is set. */
type SelectOptionOrGroup = {
  value?: string;
  label: string;
  options?: SelectOption[];
};

function NumberLiteralInput({
  value,
  onChange,
  placeholder,
  className,
  disabled,
  whiteBackground,
}: Omit<LiteralValueEntryProps, 'variableType'>) {
  // Track the number_value we last emitted so we can distinguish external
  // prop changes (undo/redo, RHS reset) from echoes of our own onChange.
  const lastEmitted = useRef(value?.number_value);
  const [text, setText] = useState(
    value?.number_value != null ? String(value.number_value) : ''
  );

  // Sync local text when value changes from outside — e.g. parent resets
  // RHS to undefined, or undo/redo swaps in a different number. Changes
  // we just emitted ourselves match lastEmitted and are ignored, so
  // intermediate text like "12." isn't clobbered by the round-trip.
  useEffect(() => {
    const n = value?.number_value;
    if (n !== lastEmitted.current) {
      setText(n != null ? String(n) : '');
      lastEmitted.current = n;
    }
  }, [value?.number_value]);

  return (
    <input
      type='number'
      step='any'
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const parsed = parseFloat(e.target.value);
        if (!Number.isNaN(parsed)) {
          // Record before calling parent so the useEffect sees a match on echo.
          lastEmitted.current = parsed;
          onChange({ type: VariableType.NUMBER, number_value: parsed });
        }
      }}
      placeholder={placeholder}
      className={[className, whiteBackground ? rhsStyles.whiteBackground : '']
        .filter(Boolean)
        .join(' ')}
      disabled={disabled}
    />
  );
}
/**
 * RHS literal value picker. Renders a different input based on lhsType:
 *   DATE_TIME → DatePicker (calendar + time)
 *   NUMBER    → InputNumber
 *   default   → Select dropdown if options are provided, plain Input otherwise
 * `forceTextInput` overrides all of the above with a plain text Input,
 * regardless of lhsType or options — see its doc comment.
 */
export function RhsValuePicker({
  lhsType,
  options,
  grouped = false,
  value,
  onChange,
  disabled = false,
  whiteBackground = false,
  placeholder,
  recurringWindowExpr,
  dateTimeSetExpr,
  onDateTimeSetExprChange,
  isMultiValue = false,
  listValue,
  onListChange,
  forceTextInput = false,
  isTeamsField = false,
}: RhsValuePickerProps) {
  const showTeamGroups =
    isTeamsField && isMultiValue && !!onListChange && !forceTextInput;
  const teamOptionValues = showTeamGroups
    ? (grouped
        ? ((options ?? []) as GroupedFieldOption[]).flatMap((g) => g.options)
        : ((options ?? []) as FieldOption[])
      ).map((o) => o.value)
    : [];
  const teamGroups = useTeamGroups(teamOptionValues, showTeamGroups);

  const bgStyle = whiteBackground
    ? { backgroundColor: 'var(--surface-container)' }
    : undefined;

  // Free-text search pattern (Contains, Starts With, Matches Regex, …) on a
  // company-defined-enum field — bypass the value dropdown entirely.
  if (forceTextInput) {
    return (
      <CustomInput
        className='custom-input form-input-style'
        border='none'
        placeholderColor='var(--text-secondary)'
        placeholder='Enter value'
        value={value?.string_value ?? ''}
        onChange={(e) =>
          onChange({
            type: VariableType.STRING,
            string_value: e.target.value,
          })
        }
        disabled={disabled}
        bgColor={whiteBackground ? 'var(--surface-container)' : undefined}
      />
    );
  }

  // Multi-select for CONTAINS / NOT_CONTAINS / IN_LIST / NOT_IN_LIST
  if (isMultiValue && options && onListChange) {
    const selectedValues = listValue?.map((v) => v.string_value ?? '') ?? [];
    const flatOpts = grouped
      ? (options as GroupedFieldOption[]).flatMap((g) => g.options)
      : (options as FieldOption[]);
    const baseOptions: SelectOptionOrGroup[] = grouped
      ? (options as GroupedFieldOption[]).map((g) => ({
          label: g.label,
          options: g.options.map((o) => ({
            value: o.value,
            label: o.label,
          })),
        }))
      : flatOpts.map((o) => ({
          value: o.value,
          label: o.label,
        }));
    const hasGroups = teamGroups.length > 0;
    // Group rows sit above the teams; they are never part of the value.
    const selectOptions: SelectOptionOrGroup[] = hasGroups
      ? [
          {
            label: teamPickerSectionLabels().groups,
            options: teamGroups.map((group) => ({
              value: toGroupValue(group.id),
              label: `${group.name} (${group.team_ids.length})`,
            })),
          },
          // The teams field is flat; head it so teams don't read as groups.
          ...(grouped
            ? baseOptions
            : [{ label: teamPickerSectionLabels().teams, options: flatOpts }]),
        ]
      : baseOptions;
    const memberLabel = (id: string): string | undefined =>
      flatOpts.find((o) => o.value === id)?.label;

    return (
      <Select
        mode='multiple'
        value={selectedValues}
        disabled={disabled}
        placeholder={placeholder ?? 'Select...'}
        style={{ width: '100%', ...bgStyle }}
        className='lb-select'
        suffixIcon={<CustomSelectIcon />}
        getPopupContainer={(trigger) => trigger.parentElement || document.body}
        options={selectOptions}
        filterOption={
          hasGroups
            ? (input, option): boolean => {
                const optionValue = String(option?.value ?? '');
                if (isGroupValue(optionValue)) {
                  const group = teamGroups.find(
                    (g) => g.id === groupIdFromValue(optionValue)
                  );
                  return (
                    !!group && groupMatchesSearch(group, input, memberLabel)
                  );
                }
                return String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.trim().toLowerCase());
              }
            : undefined
        }
        optionRender={
          hasGroups
            ? (option): React.ReactNode => {
                const optionValue = String(option.value ?? '');
                if (!isGroupValue(optionValue)) return option.label;
                const group = teamGroups.find(
                  (g) => g.id === groupIdFromValue(optionValue)
                );
                const state = group
                  ? groupSelectionState(group, selectedValues)
                  : { checked: false, indeterminate: false };
                return (
                  <Checkbox
                    className={rhsStyles.groupCheckbox}
                    checked={state.checked}
                    indeterminate={state.indeterminate}
                  >
                    {option.label}
                  </Checkbox>
                );
              }
            : undefined
        }
        tagRender={({ label, onClose }) => (
          <SelectTag
            label={label}
            onClear={onClose}
            style={{ margin: '2px' }}
          />
        )}
        onChange={(picked: string[]) => {
          // In the team picker, a group toggles its teams instead of being
          // kept as a value. Other enums keep any choice verbatim, even one
          // that happens to start with the group prefix.
          const groupValue = hasGroups ? picked.find(isGroupValue) : undefined;
          let vals = picked;
          if (groupValue) {
            const group = teamGroups.find(
              (g) => g.id === groupIdFromValue(groupValue)
            );
            vals = group
              ? toggleGroupSelection(group, selectedValues)
              : selectedValues;
          }
          onListChange(
            vals.map((v): VariableValue => ({
              string_value: v,
              type: lhsType ?? VariableType.STRING,
            }))
          );
        }}
      />
    );
  }

  if (
    lhsType === VariableType.DATE_TIME ||
    lhsType === VariableType.DATE_TIME_RANGE
  ) {
    if (!dateTimeSetExpr || !onDateTimeSetExprChange) return null;
    return (
      <DateTimeConditionValueField
        dateTimeSetExpr={dateTimeSetExpr}
        onChange={onDateTimeSetExprChange}
        legacyRecurringWindowExpr={recurringWindowExpr}
        legacyLiteralValue={value}
        disabled={disabled}
        placeholder={placeholder ?? 'Select...'}
        style={bgStyle}
      />
    );
  }

  if (lhsType === VariableType.BOOL) {
    const current = value?.bool_value;
    return (
      <>
        <Select
          placeholder='Select...'
          onChange={(e) =>
            onChange({
              type: VariableType.BOOL,
              bool_value: e === 'true',
            })
          }
          value={current === undefined ? '' : current ? 'true' : 'false'}
          disabled={disabled}
          className='lb-select'
          suffixIcon={<CustomSelectIcon />}
          getPopupContainer={(trigger) =>
            trigger.parentElement || document.body
          }
        >
          <Option value='' disabled>
            Select…
          </Option>
          <Option value='true'>True</Option>
          <Option value='false'>False</Option>
        </Select>
      </>
    );
  }

  if (lhsType === VariableType.NUMBER) {
    return (
      <NumberLiteralInput
        value={value}
        onChange={onChange}
        placeholder={'Enter number'}
        className={'lb-number-input'}
        disabled={disabled}
        whiteBackground={whiteBackground}
      />
    );
  }

  if (lhsType === VariableType.ALERT_TYPE) {
    return (
      <AlertTypeValuePicker
        value={value?.string_value ?? ''}
        onChange={(alertType) =>
          onChange(
            alertType
              ? { string_value: alertType, type: VariableType.ALERT_TYPE }
              : null
          )
        }
        disabled={disabled}
        style={bgStyle}
        rhsOptions={options as AlertPhaseOption[] | undefined}
      />
    );
  }

  if (lhsType === VariableType.TEXT) {
    return (
      <CustomInput
        className='custom-input form-input-style'
        border='none'
        placeholderColor='var(--text-secondary)'
        placeholder={'Enter value'}
        value={value?.string_value ?? ''}
        onChange={(e) =>
          onChange({
            type: VariableType.STRING,
            string_value: e.target.value,
          })
        }
        disabled={disabled}
        bgColor={whiteBackground ? 'var(--surface-container)' : undefined}
      />
    );
  }

  // Default: Select dropdown — grouped or flat
  if (grouped) {
    const groupedOpts = options as GroupedFieldOption[] | undefined;
    // Find the selected label by scanning all groups
    let selectedLabel: string | undefined;
    if (value?.string_value) {
      for (const g of groupedOpts ?? []) {
        const match = g.options?.find((o) => o.value === value.string_value);
        if (match) {
          selectedLabel = match.label;
          break;
        }
      }
    }
    return (
      <Select
        value={value?.string_value || undefined}
        disabled={disabled}
        placeholder={placeholder ?? 'Select...'}
        style={{ width: '100%', ...bgStyle }}
        className='lb-select'
        suffixIcon={<CustomSelectIcon />}
        getPopupContainer={() => document.body}
        popupMatchSelectWidth={true}
        listHeight={300}
        options={groupedOpts?.map((g) => ({
          label: g.label,
          options:
            g.options?.map((o) => ({ value: o.value, label: o.label })) ?? [],
        }))}
        onChange={(v) =>
          onChange({ string_value: v, type: lhsType ?? VariableType.STRING })
        }
        labelRender={() =>
          selectedLabel ? (
            <SelectTag label={selectedLabel} onClear={() => onChange(null)} />
          ) : (
            <span className={rhsStyles.placeholder}>
              {placeholder ?? 'Select...'}
            </span>
          )
        }
      />
    );
  }

  // Flat Select
  const flatOptions = options as FieldOption[] | undefined;
  const selectedOption = flatOptions?.find(
    (o) => o.value === value?.string_value
  );

  return (
    <Select
      value={value?.string_value || undefined}
      disabled={disabled}
      placeholder={placeholder ?? 'Select...'}
      style={{ width: '100%', ...bgStyle }}
      className='lb-select'
      suffixIcon={<CustomSelectIcon />}
      popupMatchSelectWidth={true}
      listHeight={250}
      getPopupContainer={(trigger) => trigger.parentElement || document.body}
      options={flatOptions?.map((o) => ({ value: o.value, label: o.label }))}
      onChange={(v) =>
        onChange({ string_value: v, type: lhsType ?? VariableType.STRING })
      }
      labelRender={() =>
        selectedOption ? (
          <SelectTag
            label={selectedOption.label}
            onClear={() => onChange(null)}
          />
        ) : (
          <span className={rhsStyles.placeholder}>
            {placeholder ?? 'Select...'}
          </span>
        )
      }
    />
  );
}
